import { useEffect, useRef, type ReactNode } from 'react';
import { ChatMessageBubble } from './chat-message';
import type { ChatMessage } from './types';

interface ChatMessagesProps {
  messages: ChatMessage[];
  isTyping?: boolean;
  agentInitials?: string;
  typingLabel?: string;
  footer?: ReactNode;
}

function TypingIndicator({ initials, label }: { initials?: string; label?: string }) {
  return (
    <div className="flex items-end gap-2 self-start animate-in fade-in slide-in-from-bottom-2 duration-300">
      {initials && (
        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground dark:bg-white/[0.1] dark:text-white/50">
          {initials}
        </div>
      )}
      <div className="flex items-center gap-1 rounded-xl bg-muted/60 px-4 py-3 dark:bg-white/[0.08]" role="status" aria-label={label ?? 'Agent is typing'}>
        <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/40 dark:bg-white/40 [animation-delay:0ms]" />
        <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/40 dark:bg-white/40 [animation-delay:150ms]" />
        <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/40 dark:bg-white/40 [animation-delay:300ms]" />
      </div>
    </div>
  );
}

export function ChatMessages({ messages, isTyping, agentInitials, typingLabel, footer }: ChatMessagesProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages.length, isTyping]);

  return (
    <div
      ref={scrollRef}
      className="flex flex-1 flex-col overflow-y-auto bg-background dark:bg-transparent"
    >
      {/* Messages */}
      <div className="flex flex-1 flex-col gap-3 px-6 py-5">
        {messages.map((msg) => (
          <div key={msg.id} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <ChatMessageBubble message={msg} />
          </div>
        ))}
        {isTyping && <TypingIndicator initials={agentInitials} label={typingLabel} />}
      </div>

      {/* Suggestions + branding at end of scroll content */}
      {footer}
    </div>
  );
}
